import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { http } from "@/lib/http";

export type OrgRole = "manager" | "driver";

export interface OrgMembership {
  organization_id: number;
  role: OrgRole;
}

export interface AuthUser {
  id: number;
  username: string;
  // "readonly_admin" is a full-fleet auditor: it sees every admin page but the
  // backend rejects all its writes. is_system_admin is false for it.
  role: "admin" | "user" | "readonly_admin";
  tier?: string;
  // Recovery destinations for self-service password reset. Present when
  // configured; used to enable/disable + choose the reset channel.
  email?: string | null;
  email_verified?: boolean;
  phone?: string | null;
  phone_verified?: boolean;
  is_system_admin?: boolean;
  // M:N — there is NO singular organization_id/_name. Every membership (a user may
  // be manager in some orgs and driver in others) is listed here.
  organization_memberships?: OrgMembership[];
  // Frontend feature flags (navigation/UI only; backend stays authoritative).
  features?: {
    sms_password_reset?: boolean;
    google_login?: boolean;
  };
  /** External-IdP links on this account (e.g. ["google"]). */
  linked_providers?: string[];
  google_linked?: boolean;
  /** Platform driver persona (every non-admin account). Identity only — never an
   *  access or payment verdict. */
  driver_profile?: { user_id: number; created_at: string; updated_at: string } | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  /** Global platform (system) administrator with full write access. */
  isAdmin: boolean;
  /** Read-only platform administrator (auditor): sees admin pages, cannot write. */
  isReadOnlyAdmin: boolean;
  /** May VIEW the system-admin pages: a full admin OR a read-only admin. */
  isAdminViewer: boolean;
  isFree: boolean;
  /** Member of some organization (manager OR driver). */
  isOrgUser: boolean;
  /** Manager of at least one organization (org-management access). */
  isManager: boolean;
  /** An org member who is NOT a manager and NOT a system admin (restricted). */
  isDriver: boolean;
  /** Organization ids the user may MANAGE (system admins manage all → empty here). */
  managerOrgIds: number[];
  /** SMS password-reset channel is enabled (drives SMS opt-in UI visibility). */
  smsPasswordResetEnabled: boolean;
  /** "Sign in with Google" is configured (drives the Connect-Google UI). */
  googleLoginEnabled: boolean;
  /** This account currently has Google linked. */
  googleLinked: boolean;
  isLoading: boolean;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAdmin: false,
  isReadOnlyAdmin: false,
  isAdminViewer: false,
  isFree: false,
  isOrgUser: false,
  isManager: false,
  isDriver: false,
  managerOrgIds: [],
  smsPasswordResetEnabled: false,
  googleLoginEnabled: false,
  googleLinked: false,
  isLoading: true,
  refetch: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const data = await http<{ user: AuthUser }>("/api/auth/me");
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Global system admin (authoritative field, with role fallback for older API).
  const isAdmin = user?.is_system_admin ?? (user?.role === "admin");
  // Read-only admin: NOT a system admin (is_system_admin is false), so isAdmin
  // stays the "full write access" gate; isAdminViewer is the "may view" gate.
  const isReadOnlyAdmin = user?.role === "readonly_admin";
  const isAdminViewer = isAdmin || isReadOnlyAdmin;
  const isFree = !isAdminViewer && (user?.tier === "free" || !user?.tier);
  // M:N — a user may be manager in some orgs and driver in others. managerOrgIds
  // is the set of orgs they may MANAGE (drives the Special Rates org selector).
  const memberships = user?.organization_memberships ?? [];
  const managerOrgIds = memberships.filter((m) => m.role === "manager").map((m) => m.organization_id);
  const isManager = !isAdmin && managerOrgIds.length > 0;
  const isOrgUser = memberships.length > 0;
  // Driver-only: a member of some org but a manager of NONE (and not a sys admin).
  const isDriver = !isAdmin && !isManager && isOrgUser;
  const smsPasswordResetEnabled = Boolean(user?.features?.sms_password_reset);
  const googleLoginEnabled = Boolean(user?.features?.google_login);
  const googleLinked = Boolean(user?.google_linked);

  return (
    <AuthContext.Provider
      value={{ user, isAdmin, isReadOnlyAdmin, isAdminViewer, isFree, isOrgUser, isManager, isDriver, managerOrgIds, smsPasswordResetEnabled, googleLoginEnabled, googleLinked, isLoading, refetch: fetchUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
